import { Audio } from "expo-av";
import { useState, useCallback, useRef } from "react";
import debounce from "lodash/debounce";

interface VoiceAudioPlayerState {
  isPlaying: boolean;
  isLoading: boolean;
  voiceId: string | null;
  playbackRate: number;
}

export function useVoiceAudioPlayer() {
  const [state, setState] = useState<VoiceAudioPlayerState>({
    isPlaying: false,
    isLoading: false,
    voiceId: null,
    playbackRate: 1,
  });
  const soundRef = useRef<Audio.Sound | null>(null);

  async function playAudio(
    audioUrl: string,
    voiceId: string,
    playbackRate: number
  ) {
    try {
      // If already playing the same voice sample, stop it
      if (state.isPlaying && state.voiceId === voiceId) {
        await stopAudio();
        return;
      }

      // If different voice, stop previous audio
      if (soundRef.current) {
        await stopAudio();
      }

      setState((prev) => ({ ...prev, isLoading: true }));

      // Load the sound
      const { sound } = await Audio.Sound.createAsync(
        { uri: audioUrl },
        { shouldPlay: false, positionMillis: 0 }
      );
      sound.setPositionAsync(0);
      sound.setRateAsync(playbackRate, true);
      sound.setOnPlaybackStatusUpdate((status) => {
        if (!status.isLoaded) return;
        if (status.didJustFinish) {
          setState((prev) => ({
            ...prev,
            isPlaying: false,
            isLoading: false,
          }));
        }
      });
      soundRef.current = sound;

      setState((prev) => ({
        ...prev,
        isPlaying: true,
        isLoading: false,
        voiceId,
        playbackRate,
      }));

      await sound.playAsync();
    } catch (error) {
      console.error("Failed to play audio:", error);
      setState((prev) => ({
        ...prev,
        isPlaying: false,
        isLoading: false,
      }));
    }
  }

  async function stopAudio() {
    try {
      if (soundRef.current) {
        await Promise.all([
          soundRef.current.stopAsync(),
          soundRef.current.unloadAsync(),
        ]);
        soundRef.current = null;
        setState((prev) => ({
          ...prev,
          isPlaying: false,
          voiceId: null,
        }));
      }
    } catch (error) {
      console.error("Failed to stop audio:", error);
    }
  }

  async function updatePlaybackRate(newRate: number) {
    if (soundRef.current) {
      try {
        const status = await soundRef.current.getStatusAsync();
        if (status.isLoaded) {
          debouncedUpdatePlaybackRate(soundRef.current, newRate);
        }
      } catch (error) {
        console.error("Failed to check sound status:", error);
      }
    }
  }
  const debouncedUpdatePlaybackRate = useCallback(
    debounce(async (sound: Audio.Sound, newRate: number) => {
      try {
        const status = await sound.getStatusAsync();
        if (status.isLoaded) {
          await sound.setRateAsync(newRate, true);
          setState((prev) => ({
            ...prev,
            playbackRate: newRate,
          }));
        }
      } catch (error) {
        console.error("Failed to update playback rate:", error);
      }
    }, 100),
    []
  );

  return {
    isPlaying: state.isPlaying,
    isLoading: state.isLoading,
    currentVoiceId: state.voiceId,
    playAudio,
    stopAudio,
    updatePlaybackRate,
  };
}
